import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { SystemSettingsHttpService } from '@core/http/system-settings.http.service';

import { ProductsHttpService } from '@core/http/products.http.service';
import { StackedMessagesService } from '@shared/stacked-messages/stacked-messages.service';
import { parseError } from '@state/errors.parser';

import * as systemSettingsActions from './system-settings.actions';

@Injectable()
export class SystemSettingsEffects {
  private readonly actions$ = inject(Actions);
  private readonly http = inject(SystemSettingsHttpService);
  private readonly productsHttp = inject(ProductsHttpService);
  private readonly snackService = inject(StackedMessagesService);

  getSystemSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(systemSettingsActions.getSystemSettings),
      switchMap(() => {
        return this.http.getSystemSettings().pipe(
          map(systemSettings => systemSettingsActions.getSystemSettingsSuccess({ systemSettings })),
          catchError(() =>
            of(systemSettingsActions.getSystemSettingsError({ errorMessage: 'ERROR_FETCHING_SYSTEM_SETTINGS' }))
          )
        );
      })
    )
  );

  changePIN$ = createEffect(() =>
    this.actions$.pipe(
      ofType(systemSettingsActions.changePIN),
      switchMap(({ newPIN }) => {
        return this.http.changePIN(newPIN).pipe(
          map(updatedSystemSettings => systemSettingsActions.changePINSuccess({ updatedSystemSettings })),
          catchError(() => of(systemSettingsActions.changePINError({ errorMessage: 'ERROR_CHANGING_PIN' })))
        );
      })
    )
  );

  getSelfRegistrationStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(systemSettingsActions.getSelfRegistrationStatus),
      switchMap(() => {
        return this.http.getSelfRegistrationStatus().pipe(
          map(isActive => systemSettingsActions.getSelfRegistrationStatusSuccess({ isActive })),
          catchError(() =>
            of(systemSettingsActions.getSelfRegistrationStatusError({ errorMessage: 'ERROR_FETCHING_SELF_REG_STATUS' }))
          )
        );
      })
    )
  );

  getModulesConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(systemSettingsActions.getModulesConfiguration),
      switchMap(() => {
        return this.http.getModulesConfiguration().pipe(
          map(modulesConfiguration => systemSettingsActions.getModulesConfigurationSuccess({ modulesConfiguration })),
          catchError(error => {
            const errorMessage = parseError(error, systemSettingsActions.getModulesConfiguration.type);
            this.snackService.showError({ message: errorMessage });
            return of(systemSettingsActions.getModulesConfigurationError({ errorMessage }));
          })
        );
      })
    )
  );

  setSelfRegistrationStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(systemSettingsActions.setSelfRegistrationStatus),
      switchMap(({ isActive }) => {
        return this.http.setSelfRegistrationStatus(isActive).pipe(
          map(updatedSystemSettings =>
            systemSettingsActions.setSelfRegistrationStatusSuccess({ updatedSystemSettings })
          ),
          catchError(() =>
            of(
              systemSettingsActions.setSelfRegistrationStatusError({
                errorMessage: 'ERROR_SETTING_SELF_REGISTRATION_STATUS',
              })
            )
          )
        );
      })
    )
  );

  changeMainNotificationsRecipient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(systemSettingsActions.changeMainNotificationsRecipient),
      switchMap(({ newEmail }) => {
        return this.http.changeMainNotificationRecipient(newEmail).pipe(
          map(updatedSystemSettings =>
            systemSettingsActions.changeMainNotificationsRecipientSuccess({ updatedSystemSettings })
          ),
          catchError(() =>
            of(
              systemSettingsActions.changeMainNotificationsRecipientError({
                errorMessage: 'ERROR_CHANGING_MAIN_NOTIFICATIONS_RECIPIENT',
              })
            )
          )
        );
      })
    )
  );

  changeAdditionalRecipients$ = createEffect(() =>
    this.actions$.pipe(
      ofType(systemSettingsActions.changeAdditionalRecipients),
      switchMap(({ recipientsEmails }) => {
        return this.http.changeAdditionalRecipients(recipientsEmails).pipe(
          map(updatedSystemSettings =>
            systemSettingsActions.changeAdditionalRecipientsSuccess({ updatedSystemSettings })
          ),
          catchError(() =>
            of(
              systemSettingsActions.changeAdditionalRecipientsError({
                errorMessage: 'ERROR_CHANGING_ADDITIONAL_RECIPIENTS',
              })
            )
          )
        );
      })
    )
  );

  changeSecurityRecipients$ = createEffect(() =>
    this.actions$.pipe(
      ofType(systemSettingsActions.changeSecurityRecipients),
      switchMap(({ recipientsEmails }) => {
        return this.http.changeSecurityRecipients(recipientsEmails).pipe(
          map(updatedSystemSettings =>
            systemSettingsActions.changeSecurityRecipientsSuccess({ updatedSystemSettings })
          ),
          catchError(() =>
            of(
              systemSettingsActions.changeSecurityRecipientsError({
                errorMessage: 'ERROR_CHANGING_SECURITY_RECIPIENTS',
              })
            )
          )
        );
      })
    )
  );

  getOnlineUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(systemSettingsActions.getOnlineUsers),
      switchMap(({ payload }) => {
        const { all, count, ...rest } = payload;
        return this.http.getOnlineUsers(rest).pipe(
          map(users => {
            return systemSettingsActions.getOnlineUsersSuccess({ users });
          }),
          catchError(error => {
            const errorMessage = parseError(error, systemSettingsActions.getOnlineUsers.type);
            this.snackService.showError({ message: errorMessage });
            return of(systemSettingsActions.getOnlineUsersError({ errorMessage }));
          })
        );
      })
    )
  );

  getAdmins$ = createEffect(() =>
    this.actions$.pipe(
      ofType(systemSettingsActions.getAdmins),
      switchMap(({ payload }) => {
        const { all, count, ...rest } = payload;
        return this.http.getAdmins(rest).pipe(
          map(admins => {
            return systemSettingsActions.getAdminsSuccess({ admins });
          }),
          catchError(error => {
            const errorMessage = parseError(error, systemSettingsActions.getAdmins.type);
            this.snackService.showError({ message: errorMessage });
            return of(systemSettingsActions.getAdminsError({ errorMessage }));
          })
        );
      })
    )
  );

  getAdmin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(systemSettingsActions.getAdmin),
      switchMap(({ adminId }) => {
        return this.http.getAdmin(adminId).pipe(
          map(admin => {
            return systemSettingsActions.getAdminSuccess({ admin });
          }),
          catchError(error => {
            const errorMessage = parseError(error, systemSettingsActions.getAdmin.type);
            this.snackService.showError({ message: errorMessage });
            return of(systemSettingsActions.getAdminError({ errorMessage }));
          })
        );
      })
    )
  );

  createAdmin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(systemSettingsActions.createAdmin),
      switchMap(({ opts }) => {
        return this.http.createAdmin(opts).pipe(
          map(({ user, emailSent }) => {
            if (emailSent) {
              this.snackService.showInfo({ message: 'COMMON.NOTIFICATION.ACTIVATION_EMAIL_SENT' });
            } else {
              this.snackService.showError({ message: 'EMPLOYEES.ACCOUNT-CREATED-BUT-EMAIL-SENDING-FAILED' });
            }
            return systemSettingsActions.createAdminSuccess({ createdAdmin: user });
          }),
          catchError(error => {
            const errorMessage = parseError(error, systemSettingsActions.createAdmin.type);
            this.snackService.showError({ message: errorMessage });
            return of(systemSettingsActions.createAdminError({ errorMessage }));
          })
        );
      })
    )
  );

  updateAdmin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(systemSettingsActions.updateAdmin),
      switchMap(({ opts, userId }) => {
        return this.http.updateAdmin(opts, userId).pipe(
          map(updatedAdmin => {
            return systemSettingsActions.updateAdminSuccess({ updatedAdmin });
          }),
          catchError(error => {
            const errorMessage = parseError(error, systemSettingsActions.updateAdmin.type);
            this.snackService.showError({ message: errorMessage });
            return of(systemSettingsActions.updateAdminError({ errorMessage }));
          })
        );
      })
    )
  );

  removeAdmin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(systemSettingsActions.removeAdmin),
      switchMap(({ userId }) => {
        return this.http.removeAdmin(userId).pipe(
          map(() => {
            this.snackService.showInfo({ message: 'ADMINISTRATION.ADMINS.ADMIN-REMOVED' });
            return systemSettingsActions.removeAdminSuccess({ removedAdminId: userId });
          }),
          catchError(error => {
            const errorMessage = parseError(error, systemSettingsActions.removeAdmin.type);
            this.snackService.showError({ message: errorMessage });
            return of(systemSettingsActions.removeAdminError({ errorMessage }));
          })
        );
      })
    )
  );

  resendAdminActivation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(systemSettingsActions.resendAdminActivation),
      switchMap(({ email }) => {
        return this.http.resendAdminActivation(email).pipe(
          map(({ emailSent }) => systemSettingsActions.resendAdminActivationSuccess({ emailSent })),
          catchError(error => {
            const errorMessage = parseError(error, systemSettingsActions.resendAdminActivation.type);
            return of(systemSettingsActions.resendAdminActivationError({ errorMessage }));
          })
        );
      })
    )
  );

  recreateAvatarsForAllUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(systemSettingsActions.recreateAvatarsForAllUsers),
      switchMap(() => {
        return this.http.recreateAvatarsForAllUsers().pipe(
          map(() => systemSettingsActions.recreateAvatarsForAllUsersSuccess()),
          catchError(() => {
            return of(systemSettingsActions.recreateAvatarsForAllUsersError());
          })
        );
      })
    )
  );

  getProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(systemSettingsActions.getProducts),
      switchMap(() => {
        return this.productsHttp.getProducts().pipe(
          map(products => systemSettingsActions.getProductsSuccess({ products })),
          catchError(() => {
            this.snackService.showError({ message: 'ADMINISTRATION.PRODUCTS-DETAILS.ERROR_LOADING_PRODUCTS' });
            return of(systemSettingsActions.getProductsError());
          })
        );
      })
    )
  );

  createProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(systemSettingsActions.createProduct),
      switchMap(({ opts }) => {
        return this.productsHttp.createProduct(opts).pipe(
          map(product => systemSettingsActions.createProductSuccess({ product })),
          catchError(() => {
            this.snackService.showError({ message: 'ADMINISTRATION.PRODUCTS-DETAILS.ERROR_CREATING_PRODUCT' });
            return of(systemSettingsActions.createProductError());
          })
        );
      })
    )
  );

  updateProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(systemSettingsActions.updateProduct),
      switchMap(({ opts }) => {
        return this.productsHttp.updateProduct(opts).pipe(
          map(updatedProduct => systemSettingsActions.updateProductSuccess({ updatedProduct })),
          catchError(() => {
            this.snackService.showError({ message: 'NT4.ERROR_UPDATING_PRODUCT' });
            return of(systemSettingsActions.updateProductError({ productId: opts.productId }));
          })
        );
      })
    )
  );
  getProductHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(systemSettingsActions.getProductHistory),
      switchMap(({ productId }) => {
        return this.productsHttp.getProductHistory(productId).pipe(
          map(productHistory => systemSettingsActions.getProductHistorySuccess({ productHistory })),
          catchError(() => {
            this.snackService.showError({ message: 'NT4.ERROR_LOADING_PRODUCT_CHANGES_HISTORY' });
            return of(systemSettingsActions.getProductHistoryError({ productId }));
          })
        );
      })
    )
  );

  // ############
  // PROMOTIONAL BANNER
  // ############

  setPromotionalBanner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(systemSettingsActions.setPromotionalBanner),
      switchMap(({ opts }) => {
        return this.http.setPromotionalBanner(opts).pipe(
          map(result => systemSettingsActions.setPromotionalBannerSuccess({ result })),
          catchError(error => {
            const errorMessage = parseError(error, systemSettingsActions.setPromotionalBanner.type);
            this.snackService.showError({ message: 'PROMOTIONAL_BANNER_UPDATE_FAILED' });
            return of(systemSettingsActions.setPromotionalBannerError({ errorMessage }));
          })
        );
      })
    )
  );

  getPromotionalBanner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(systemSettingsActions.getPromotionalBanner),
      switchMap(() => {
        return this.http.getPromotionalBanner().pipe(
          map(result => systemSettingsActions.getPromotionalBannerSuccess({ result })),
          catchError(error => {
            const errorMessage = parseError(error, systemSettingsActions.getPromotionalBanner.type);
            return of(systemSettingsActions.getPromotionalBannerError({ errorMessage }));
          })
        );
      })
    )
  );
}
